import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["list", "sortAlphaBtn", "sortCountBtn", "selectedCount", "providersTab", "statsTab", "providersPane", "statsPane", "providersContent", "statsContent", "statsRow", "hiddenCount", "availableCount"]
  static values = { listUrl: String, statsUrl: String }

  #providersLoaded = false
  #statsLoaded = false
  #statsSortCol = "activity"
  #statsSortDir = "desc"

  connect() {
    if (localStorage.getItem('providers_tab') === 'stats') {
      this.providersPaneTarget.classList.add("d-none")
      this.statsPaneTarget.classList.remove("d-none")
      this.providersTabTarget.classList.remove("active")
      this.statsTabTarget.classList.add("active")
      this.#loadStats()
    } else {
      this.#loadProviders()
    }
  }

  sortAlpha() {
    this.#sortItems((a, b) => a.dataset.name.localeCompare(b.dataset.name))
    this.#setActiveSort(this.sortAlphaBtnTarget)
    this.#saveSort(0)
  }

  sortCount() {
    this.#sortItems((a, b) => (parseInt(b.dataset.count) - parseInt(a.dataset.count)) || a.dataset.name.localeCompare(b.dataset.name))
    this.#setActiveSort(this.sortCountBtnTarget)
    this.#saveSort(1)
  }

  toggle(event) {
    const checkbox = event.target
    const providerId = event.params.id
    const wasChecked = checkbox.checked

    checkbox.disabled = true

    fetch(`/providers/toggle/${providerId}`, {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        "Accept": "application/json"
      }
    })
      .then(response => {
        if (!response.ok) throw new Error(response.statusText)
        return response.json()
      })
      .then(data => {
        checkbox.checked = data.selected
        this.#updateVisibilityCounts(data.hidden_count, data.available_count)
      })
      .catch(() => {
        checkbox.checked = !wasChecked
      })
      .finally(() => {
        checkbox.disabled = false
        this.#updateCheckboxState()
      })
  }

  showProviders() {
    this.providersPaneTarget.classList.remove("d-none")
    this.statsPaneTarget.classList.add("d-none")
    this.providersTabTarget.classList.add("active")
    this.statsTabTarget.classList.remove("active")
    localStorage.setItem('providers_tab', 'providers')
    this.#loadProviders()
  }

  showStats() {
    this.providersPaneTarget.classList.add("d-none")
    this.statsPaneTarget.classList.remove("d-none")
    this.providersTabTarget.classList.remove("active")
    this.statsTabTarget.classList.add("active")
    localStorage.setItem('providers_tab', 'stats')
    this.#loadStats()
  }

  sortStats(event) {
    const col = event.currentTarget.dataset.col

    if (col === this.#statsSortCol) {
      this.#statsSortDir = this.#statsSortDir === "asc" ? "desc" : "asc"
    } else {
      this.#statsSortCol = col
      this.#statsSortDir = col === "activity" ? "desc" : "asc"
    }

    const tbody = this.statsPaneTarget.querySelector("tbody")
    if (!tbody) return

    Array.from(this.statsRowTargets)
      .sort((a, b) => {
        if (this.#statsSortCol === "name") {
          const val = a.dataset.name.localeCompare(b.dataset.name)
          return this.#statsSortDir === "asc" ? val : -val
        }
        const aTime = a.dataset.lastAt ? new Date(a.dataset.lastAt).getTime() : null
        const bTime = b.dataset.lastAt ? new Date(b.dataset.lastAt).getTime() : null
        if (aTime === null && bTime === null) return 0
        if (aTime === null) return 1
        if (bTime === null) return -1
        return this.#statsSortDir === "asc" ? aTime - bTime : bTime - aTime
      })
      .forEach(row => tbody.appendChild(row))

    this.#updateStatsSortIcons()
  }

  #loadProviders() {
    if (this.#providersLoaded) return
    this.#providersLoaded = true

    fetch(this.listUrlValue, { headers: { "Accept": "text/html" } })
      .then(response => response.text())
      .then(html => {
        this.providersContentTarget.innerHTML = html
        this.#applySort()
        this.#updateCheckboxState()
      })
      .catch(() => { this.providersContentTarget.innerHTML = '<p class="text-muted">Failed to load.</p>' })
  }

  #loadStats() {
    if (this.#statsLoaded) return
    this.#statsLoaded = true

    fetch(this.statsUrlValue, { headers: { "Accept": "text/html" } })
      .then(response => response.text())
      .then(html => {
        this.statsContentTarget.innerHTML = html
        this.#updateStatsSortIcons()
      })
      .catch(() => { this.statsContentTarget.innerHTML = '<p class="text-muted">Failed to load.</p>' })
  }

  #applySort() {
    const sort = parseInt(localStorage.getItem('provider_sort') || '0')
    if (sort === 1) {
      this.#sortItems((a, b) => (parseInt(b.dataset.count) - parseInt(a.dataset.count)) || a.dataset.name.localeCompare(b.dataset.name))
      this.#setActiveSort(this.sortCountBtnTarget)
    } else {
      this.#setActiveSort(this.sortAlphaBtnTarget)
    }
  }

  #sortItems(compareFn) {
    const list = this.listTarget
    Array.from(list.children).sort(compareFn).forEach(item => list.appendChild(item))
  }

  #setActiveSort(activeBtn) {
    [this.sortAlphaBtnTarget, this.sortCountBtnTarget].forEach(btn => btn.classList.remove("active"))
    activeBtn.classList.add("active")
  }

  #saveSort(value) {
    localStorage.setItem('provider_sort', value)
  }

  #updateCheckboxState() {
    const checkboxes = Array.from(this.listTarget.querySelectorAll('input[type="checkbox"]'))
    const checkedCount = checkboxes.filter(cb => cb.checked).length

    if (this.hasSelectedCountTarget) this.selectedCountTarget.textContent = checkedCount
  }

  #updateVisibilityCounts(hidden, available) {
    if (hidden !== undefined && hidden !== null && this.hasHiddenCountTarget) {
      this.hiddenCountTarget.textContent = hidden.toLocaleString()
    }
    if (available !== undefined && available !== null && this.hasAvailableCountTarget) {
      this.availableCountTarget.textContent = available.toLocaleString()
    }
  }

  #updateStatsSortIcons() {
    this.statsPaneTarget.querySelectorAll("[data-col]").forEach(btn => {
      const icon = btn.querySelector("i")
      if (!icon) return
      if (btn.dataset.col === this.#statsSortCol) {
        icon.className = `bi bi-arrow-${this.#statsSortDir === "asc" ? "up" : "down"}`
      } else {
        icon.className = "bi bi-arrow-down-up"
      }
    })
  }
}
